// Pure motion-line + transition-frame builders extracted from
// history-viewer.ts. Kept separate from `history-viewer-pure.ts`
// because that module is imported by `history-viewer-fstar` (for the
// index codec); routing the F* bridge through here keeps the dep graph
// acyclic:
//
//   history-viewer.ts
//     ├─ history-viewer-pure.ts (codec + tiny helpers, no F* import)
//     ├─ history-viewer-fstar.ts (WASM bridge, imports codec from -pure)
//     └─ history-viewer-frame-pure.ts (this file; imports bridge + -pure)
//
// Tests that exercise these builders import from this module directly
// so they bypass the renderer's DOM/GSAP transitive chain.

import type { MotionKind, TransitionLineKind } from "./history-viewer-types";
import {
  computeMotionLinesWithFStar,
  computeTransitionWindowWithFStar,
} from "./history-viewer-fstar";
import { splitLines, buildTransitionLines } from "./history-viewer-pure";

// Full motion-line shape used by the renderer + buildTransitionFrame.
// Mutable structurally so existing call sites can spread/destructure
// without `readonly` propagation; treat as immutable by convention.
export interface PureMotionLine {
  key: string;
  kind: MotionKind;
  text: string;
  fromIndex: number | null;
  toIndex: number | null;
  order: number;
  staggerIndex: number;
}

// Pure transition line set assembled per-row alongside the motion-line
// stack. The renderer-side `TransitionLine` mirrors this shape.
export interface PureTransitionFrameLine {
  kind: TransitionLineKind;
  oldText: string;
  newText: string;
}

// Shape needed to drive a transition frame. Production passes
// `HistoryCommitFrame` from .dox/runtime; the pure module accepts the
// structural minimum so tests can pass plain fixtures without dragging
// the dox runtime into the import graph.
export interface PureCommitForFrame {
  readonly hash: string;
  readonly content?: string | null;
}

// Frame built by `buildTransitionFrame`. Carries enough state for the
// renderer (and tests) to stage the cross-fade animation.
export interface PureTransitionFrame<TCommit extends PureCommitForFrame> {
  from: TCommit;
  to: TCommit;
  fromContent: string;
  toContent: string;
  progress: number;
  lines: PureTransitionFrameLine[];
  motionLines: PureMotionLine[];
  oldLineCount: number;
  newLineCount: number;
  direction: 1 | -1;
  staggerCount: number;
}

const motionLineStaggerCount = (
  motionLines: readonly PureMotionLine[],
): number =>
  Math.max(
    motionLines.filter((line) => line.kind === "added").length,
    motionLines.filter((line) => line.kind === "removed").length,
    1,
  );

// Default resolver: read content straight off the commit object. The
// production viewer overrides this with a cache-backed resolver so
// payloads can omit content for everything except the active snapshot.
const inlineContentResolver = (commit: PureCommitForFrame): string | null =>
  typeof commit.content === "string" ? commit.content : null;

// Build the motion-line stack (added/removed/unchanged) for a pair of
// snapshots. Pure modulo the F*/WASM bridge — tests stub the bridge via
// `setHistoryViewerWasmModuleForTest` so this runs without a DOM.
export const buildMotionLines = (
  fromText: string,
  toText: string,
): PureMotionLine[] => {
  const fromLines = splitLines(fromText);
  const toLines = splitLines(toText);
  const fromFStar = computeMotionLinesWithFStar(fromLines, toLines);
  if (!fromFStar)
    throw new Error("[HistoryViewer] WASM unavailable: computeMotionLines");

  return fromFStar.map((line) => ({
    key:
      line.kind === "unchanged"
        ? `same:${line.order}:${line.fromIndex ?? 0}:${line.toIndex ?? 0}`
        : line.kind === "added"
          ? `add:${line.order}:${line.toIndex ?? 0}`
          : `remove:${line.order}:${line.fromIndex ?? 0}`,
    kind: line.kind,
    text:
      line.kind === "removed"
        ? (fromLines[line.fromIndex ?? 0] ?? "")
        : (toLines[line.toIndex ?? 0] ?? ""),
    fromIndex: line.fromIndex,
    toIndex: line.toIndex,
    order: line.order,
    staggerIndex: line.staggerIndex,
  }));
};

const clampInt = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

// Slice a transition window out of the commit list at scroll-position
// `progress`, then compose a frame the renderer can stage. Pure modulo
// the F*/WASM bridge for window math + motion-line diffing.
export const buildTransitionFrame = <TCommit extends PureCommitForFrame>(
  commits: readonly TCommit[],
  progress: number,
  direction: 1 | -1 = 1,
  getContent: (commit: TCommit) => string | null = inlineContentResolver,
): PureTransitionFrame<TCommit> | null => {
  if (commits.length < 2) return null;

  const clamped = clampInt(progress, 0, commits.length - 1);
  const transitionWindow = computeTransitionWindowWithFStar(
    clamped,
    commits.length,
  );
  if (!transitionWindow)
    throw new Error(
      "[HistoryViewer] WASM unavailable: computeTransitionWindow",
    );
  const lowerIndex = transitionWindow.lowerIndex;
  const upperIndex = transitionWindow.upperIndex;

  if (lowerIndex === upperIndex) return null;

  const from = commits[lowerIndex]!;
  const to = commits[upperIndex]!;
  const fromContent = getContent(from);
  const toContent = getContent(to);
  // Either endpoint not yet loaded — caller will see null and (in the
  // live viewer) trigger a fetch + redraw rather than animate against
  // missing content.
  if (fromContent === null || toContent === null) return null;

  const motionLines = buildMotionLines(fromContent, toContent);

  return {
    from,
    to,
    fromContent,
    toContent,
    progress: transitionWindow.localProgress,
    lines: buildTransitionLines(fromContent, toContent),
    motionLines,
    oldLineCount: splitLines(fromContent).length,
    newLineCount: splitLines(toContent).length,
    direction,
    staggerCount: motionLineStaggerCount(motionLines),
  };
};
